import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageType,
  QueryKeys,
  mutationHandler,
  useAppLocation,
  useAppState,
  useAppTranslation,
} from "../../../common";
import { ErrorModal, Modal } from "../../../components";
import { deleteParentAction } from "../../../actions/parent";
import { ParentEntity } from "../../../entity";

type ParentDeleteModalProps = {
  handleDeleteModalVisible: () => void;
  handleSetPage: (value: number) => void;
};

export function ParentDeleteModal(props: ParentDeleteModalProps) {
  const { t } = useAppTranslation();
  const location = useAppLocation();
  const queryClient = useQueryClient();

  const [state, setState] = useAppState({
    errorModalVisible: false,
  });

  const handleModalVisibility = () => {
    setState((prevState) => ({
      ...prevState,
      errorModalVisible: !prevState.errorModalVisible,
    }));
  };

  const deleteParent = useMutation({
    mutationKey: [QueryKeys.deleteParentMutatuionQuery],
    mutationFn: deleteParentAction,
    onSuccess() {
      props.handleDeleteModalVisible();
      queryClient.invalidateQueries({ queryKey: [QueryKeys.parentsQuery] });
      let page = 1;

      queryClient.setQueryData(
        [QueryKeys.parentsQuery],
        (old: PageType<ParentEntity>) => {
          const handler = mutationHandler({
            dataLength: old.data.length,
            pageSize: old.pageSize,
            count: old.count,
          });
          page = handler.page;

          return {
            page: old.page,
            pageSize: old.pageSize,
            count: handler.page,
            data: old.data,
          };
        }
      );
      props.handleSetPage(page);
    },
    onError() {
      handleModalVisibility();
    },
  });

  const handleDeleteParent = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    deleteParent.mutate({ id: location.state?.id });
  };

  return (
    <div>
      <section>
        <Modal
          onClose={props.handleDeleteModalVisible}
          title={t("parent-page-delete-modal-title")}
          footer
          submitText={t("parent-page-delete-submit-button-title")}
          cancelText={t("parent-page-delete-cancel-button-title")}
          handleSubmit={handleDeleteParent}
          loading={deleteParent.isPending}
        >
          <span>{t("parent-page-delete-parent-question-label")}</span>
        </Modal>
      </section>
      <section>
        {state.errorModalVisible && (
          <ErrorModal
            error={deleteParent.error}
            onClose={handleModalVisibility}
            title={t("parent-page-delete-parent-error-modal-title")}
          />
        )}
      </section>
    </div>
  );
}
